/**
 * authSessionUtils
 *
 * Utility functions for managing user session data and storage cleanup.
 * Extracted from AuthContext for modularity.
 */

// Keys for Stripe checkout state
export const STRIPE_CHECKOUT_STATE_KEY = 'stripe_checkout_state';
export const STRIPE_SESSION_ID_KEY = 'stripe_session_id';

/**
 * Comprehensive function to clear user-specific session/local storage
 * Used during sign-out and account deletion
 */
export const clearUserSessionData = () => {
  const itemsToRemove = [
    'currentUserId', 'userEmail', 'userDisplayName', 'currentNamespace',
    'user_profile_cache', 'user_profile_timestamp', 'user_data_cache', 'user_data_timestamp',
    'sub_data_cache', 'sub_data_timestamp',
    'refrral_user_profile', 'refrral_subscription_data', 'refrral_user_cache',
    'cache_user_profile', 'auth_cache_timestamp', 'subscription_cache_timestamp',
    'selectedPlan', 'just_signed_up', 'coming_from_signup',
    'stripeSessionId', 'stripeSessionCreatedAt',
    'stripe_session_id', 'stripe_checkout_initiated', 'stripe_checkout_auth_state',
    'stripe_checkout_return_path', 'post_login_redirect', 'post_login_session_id',
    'stripe_auth_uid', 'stripe_auth_email', 'stripe_auth_timestamp',
    'auth_for_checkout', 'stripe_auto_login_needed',
    'refrral_auth_error', 'auth_error_timestamp',
    'auth_redirect_state', 'auth_redirect_destination',
    'returnToSettings', 'immediate_downgrade',
    'last_redirect_time', 'google_signin_error'
  ];

  itemsToRemove.forEach(item => {
    localStorage.removeItem(item);
  });

  // Also clear session storage items that could cause redirect issues
  const sessionItemsToRemove = [
    'isSigningOut', 'accountDeletionInProgress', 'justLoggedIn',
    'in_stripe_checkout_flow', 'checkout_auth_uid', 'checkout_auth_email',
    'stripe_checkout_cancelled', 'googleSignInInProgress', 'isTransitioning',
    'auth_redirect_in_progress', 'needs_auth_refresh', 'coming_from_signup'
  ];

  sessionItemsToRemove.forEach(item => {
    sessionStorage.removeItem(item);
  });

  console.log("authSessionUtils: Cleared user session data and all storage items");
};

/**
 * Clear all auth-related errors from storage
 */
export const clearAllStoredAuthErrors = () => {
  const authErrorKeys = [
    'refrral_auth_error',
    'auth_error',
    'stripe_auth_error',
    'auth_error_timestamp'
  ];
  
  authErrorKeys.forEach(key => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
  
  console.log("authSessionUtils: Cleared all stored auth errors");
};

/**
 * Store auth backup data for recovery after redirects
 */
export const storeAuthBackup = (user, additionalData = {}) => {
  try {
    const authBackup = {
      uid: user.uid,
      email: user.email,
      timestamp: Date.now(),
      ...additionalData
    };
    localStorage.setItem('auth_backup', JSON.stringify(authBackup));
    console.log('authSessionUtils: Auth backup saved to localStorage');
    return true;
  } catch (storageError) {
    console.warn('authSessionUtils: Failed to save auth backup:', storageError);
    return false;
  }
};

/**
 * Get auth backup data if available
 */
export const getAuthBackup = () => {
  try {
    const authBackupJson = localStorage.getItem('auth_backup') || localStorage.getItem('auth_for_checkout');
    if (authBackupJson) {
      return JSON.parse(authBackupJson);
    }
  } catch (e) {
    console.error('authSessionUtils: Error parsing auth backup:', e);
  }
  return null;
};

/**
 * Store Stripe checkout state before redirect
 */
export const storeStripeCheckoutState = (sessionId, userUid) => {
  const checkoutState = {
    inProgress: true,
    sessionId: sessionId,
    uid: userUid,
    timestamp: Date.now(),
    returnPath: window.location.pathname
  };

  localStorage.setItem(STRIPE_CHECKOUT_STATE_KEY, JSON.stringify(checkoutState));
  localStorage.setItem(STRIPE_SESSION_ID_KEY, sessionId);
  
  console.log('authSessionUtils: Stripe checkout flow initiated:', checkoutState);
  return checkoutState;
};

/**
 * Get stored Stripe checkout state
 */
export const getStripeCheckoutState = () => {
  const storedState = localStorage.getItem(STRIPE_CHECKOUT_STATE_KEY);
  if (storedState) {
    try {
      return JSON.parse(storedState);
    } catch (e) {
      console.error('authSessionUtils: Error parsing stored checkout state:', e);
      localStorage.removeItem(STRIPE_CHECKOUT_STATE_KEY);
    }
  }
  return null;
};

/**
 * Clear Stripe checkout state
 */
export const clearStripeCheckoutState = () => {
  localStorage.removeItem(STRIPE_CHECKOUT_STATE_KEY);
  localStorage.removeItem(STRIPE_SESSION_ID_KEY);
  console.log('authSessionUtils: Stripe checkout state cleared');
};

/**
 * Check if user was just created (within last N seconds)
 */
export const isRecentlyCreatedUser = (firebaseUser, withinSeconds = 10) => {
  const userCreationTime = firebaseUser?.metadata?.creationTime;
  if (!userCreationTime) return false;
  
  const timeSinceCreation = Date.now() - new Date(userCreationTime).getTime();
  return timeSinceCreation < (withinSeconds * 1000);
};

/**
 * Set auth persistence flag
 */
export const setAuthPersistence = (type = 'local') => {
  localStorage.setItem('auth_persistence', type);
  sessionStorage.setItem('auth_is_persistent', 'true');
};

